import MyMapsDialog, { IMyMapsDialog } from "./mymaps-dialog";
import { _ } from "~/i18n";
import { folderCreate, likeMove, setFolderData } from "~/util/providers/mapybox";
import { mapyboxNotification } from "~/util/mapybox";
import MyMapsFolderSelectCreate from "../mymaps/folder-select-create";

interface IMyMapsMoveItems extends Pick<IMyMapsDialog, "onCancel" | "onClose"> {
	ids: Array<string>;
	folder?: {
		id: string;
		title: string;
	};
	parentId: string;
	parentTitle: string;
	onSave?: () => void;
	onError?: () => void;
}

export default class MyMapsMoveItems {
	_opts: IMyMapsMoveItems;

	_dialog: MyMapsDialog;

	_folderSelectCreate: MyMapsFolderSelectCreate;

	constructor(data: IMyMapsMoveItems) {
		this._opts = {
			ids: [],
			folder: null,
			onSave: () => {},
			onCancel: () => {},
			onClose: () => {},
			onError: () => {},
			...data,
		};

		this._build();
	}

	open() {
		this._dialog?.open();
	}

	destroy() {
		this._dialog?.destroy();
	}

	_closeDialog(force?: boolean) {
		this._opts.onClose();
		this._dialog.close(force);
	}

	_build() {
		const dialog = new MyMapsDialog({
			titles: {
				main: [_("save")],
				saveBtn: _("save"),
				cancelBtn: _("cancel"),
				throwAwayTitle: _("note.throwAwayNote"),
				throwAwayConfirm: _("detail.content.review2.throwAway"),
			},
			onThrowAwayTest: () => true,
			onSave: () => {
				this._onSave();
			},
			onCancel: () => {
				this._opts.onCancel();
				this._closeDialog(true);
			},
		});
		const mainContent = dialog.contents[0];

		mainContent.classList.add("mymaps__createNoteDialog__mainContent");

		this._folderSelectCreate = new MyMapsFolderSelectCreate({
			folder: this._opts.folder,
			onChange: state => {
				this._dialog.setSaveBtnTitle = state.createNewFolder ? _("mymap.folders.createAndSave") : _("save");
			},
		});

		mainContent.append(this._folderSelectCreate.container);

		this._dialog = dialog;
	}

	async _onSave() {
		const folderState = this._folderSelectCreate.state;
		let folderId = folderState.folderId;
		let isError = false;

		this._dialog.error = "";
		this._dialog.loading = true;

		// nova slozka
		if (folderState.createNewFolder) {
			try {
				const createData = await folderCreate(folderState.newFolderTitle);

				folderId = createData.id;
				mapyboxNotification({
					msg: _("mymap.folders.createOk", { folder: folderState.newFolderTitle }),
				});
			} catch (exc) {
				this._dialog.error = _("mymap.folders.createError");
				this._dialog.loading = false;

				return;
			}
		}

		// presun
		try {
			await likeMove(this._opts.ids, folderId);

			Mapy.ProviderMapyBox.removeFolderFromCache(folderId);
			Mapy.ProviderMapyBox.folderList(folderId).then(() => {
				this._afterMove(folderId, folderState.folderTitle);
			}, () => {
				this._afterMove(folderId, folderState.folderTitle);
			});
		} catch (errData) {
			let duplicateLink = false;

			if ("errors" in errData && errData.errors.length > 0) {
				errData.errors.forEach((errItem: { status: number; }) => {
					if (errItem.status === 409) {
						duplicateLink = true;
					}
				});
			}

			if (duplicateLink) {
				mapyboxNotification({
					msg: _("mymap.addPopup.tracklinkConflict"),
					folderTitle: folderState.folderTitle,
					folderId,
					type: "info",
				});
				this._sendAnalytics();
			} else {
				isError = true;
				mapyboxNotification({
					msg: _("mymap.addPopup.moveError", {
						count: this._opts.ids.length,
					}),
					type: "error",
				});
			}
		}

		if (isError) {
			this._opts.onError();
		} else {
			setFolderData({
				id: folderId,
				title: folderState.folderTitle,
			});

			this._opts.onSave();
		}

		this._closeDialog(true);
	}

	_afterMove(folderId: string, folderName: string) {
		mapyboxNotification({
			msg: _("mymap.addPopup.moveOk", {
				count: this._opts.ids.length,
			}),
			folderId,
			folderTitle: folderName,
		});
		this._sendAnalytics();
	}

	_sendAnalytics() {
		Mapy.getComponent("logger").log("my_maps_items_move", {
			type: "move",
			...this._opts.ids.length > 1
				? {
					length: this._opts.ids.length,
				}
				: {},
		});
	}
}
